import React, { useMemo } from "react"

import { useQuery, useQueryClient } from "@tanstack/react-query"
import { MSKType, SponsorT } from "types/sponsor"

import { useAuth } from "src/context/AuthContext"
import { sessionScopedStorage } from "src/utils/storage/mmkv"
import { useCurrentConversation } from "./useConversation"
import { useMember } from "./useUser"

export function useSTTAvailable() {
  const { api } = useAuth()
  return useQuery({
    queryFn: async () => {
      const response = await api.getSTTAvailable()
      if (response.kind !== "ok") {
        throw new Error("Failed to fetch stts")
      }
      return response.sttConfig
    },
    queryKey: ["stts"],
  })
}

export function useTTSAvailable() {
  const { api } = useAuth()
  return useQuery({
    queryFn: async () => {
      const response = await api.getTTSAvailable()
      if (response.kind !== "ok") {
        throw new Error("Failed to fetch tts")
      }
      return response.ttsConfig
    },
    queryKey: ["tts"],
  })
}

export function useMskAvailableQuery() {
  const { api, isAuthenticated, sponsorId } = useAuth()
  return useQuery({
    queryFn: async () => {
      const response = await api.getMSKTypes()
      if (response.kind !== "ok") {
        throw new Error(`Failed to fetch mskTypes: ${response.kind}`)
      }
      return response.mskTypes
    },
    queryKey: ["msk"],
    enabled: !!api && isAuthenticated && !!sponsorId && sponsorId !== "unknown-sponsor",
  })
}

export function useMSKAvailable(): MSKType[] {
  const mskQuery = useMskAvailableQuery()

  if (mskQuery.isLoading || mskQuery.isError) {
    return []
  }

  if (!mskQuery.data) {
    console.warn(
      "[useMSKAvailable] No MSK data returned from query (mskQuery.data is falsy), returning empty list.",
    )
    return []
  }

  return mskQuery.data
}

export function useMemberFactFieldsQuery() {
  const { api, sponsorId } = useAuth()

  return useQuery({
    queryFn: async () => {
      const response = await api.getFactFields()
      if (response.kind !== "ok") {
        throw new Error("Failed to fetch fact fields")
      }
      return response.fields
    },
    queryKey: ["member-fact-fields"],
    enabled: !!api && !!sponsorId && sponsorId !== "unknown-sponsor",
  })
}

export function useMemberFactFields() {
  const query = useMemberFactFieldsQuery()

  if (query.isLoading) {
    throw new Error("No member fact fields available because the query is loading.")
  }

  if (!query.data) {
    throw new Error("No member fact fields found.")
  }

  return query.data
}

export function useCurrentMsk(): MSKType | undefined {
  const mskList = useMSKAvailable()
  const member = useMember()

  if (!member) {
    return undefined
  }

  if (!mskList || mskList.length === 0) {
    return undefined
  }

  const msk = mskList.find((m) => m.id === member.mskId)

  if (!msk) {
    console.log("[useCurrentMsk] Returning undefined because no matching MSK found.", {
      memberMskIdAttempted: member.mskId,
      mskListIdsAvailable: mskList.map((m) => m.id),
    })
    return undefined
  }

  return msk
}

export function useESKAvailable() {
  const { api } = useAuth()
  return useQuery({
    queryFn: async () => {
      const response = await api.getESKTypes()
      if (response.kind !== "ok") {
        throw new Error("Failed to fetch esk types")
      }
      return response.eskTypes
    },
    queryKey: ["esk"],
  })
}

export function useESKTtsId(activeEskSponsor: string, activeAvatarId: string | number) {
  const eskData = useESKAvailable().data

  return useMemo(() => {
    // Find the sponsor
    const sponsor = eskData?.find((s) => s.id === activeEskSponsor)
    // Find the ESK type within that sponsor's types
    const esk = sponsor?.esk_types?.find((t) => t.avatar_id === activeAvatarId.toString())
    // Return the tts_id
    return esk?.tts_id
  }, [eskData, activeEskSponsor, activeAvatarId])
}

export function useCurrentTTS() {
  const { data: currentConversation } = useCurrentConversation()
  const ttsConfig = useTTSAvailable()
  const activeAvatarId = currentConversation?.sidekick_avatar_id
  const activeESKSponsor = currentConversation?.active_esk_sponsor
  const ttsId = useESKTtsId(activeESKSponsor ?? "", activeAvatarId ?? "0")
  return ttsConfig.data?.find((tts) => tts.id === ttsId)
}

export function useClearConfigCache() {
  const queryClient = useQueryClient()

  return React.useCallback(() => {
    queryClient.removeQueries({ queryKey: ["msk"] })
    queryClient.removeQueries({ queryKey: ["esk"] })
    queryClient.removeQueries({ queryKey: ["tts"] })
    queryClient.removeQueries({ queryKey: ["stts"] })
    queryClient.removeQueries({ queryKey: ["member-fact-fields"] })
    // FIXME: both these features seem to be unused
    queryClient.removeQueries({ queryKey: ["todos"] })
    queryClient.removeQueries({ queryKey: ["sentiment"] })
  }, [queryClient])
}

export function getCurrentSponsor() {
  // sponsorId lives in the per-tab MMKV so different tabs can be in different
  // sponsors. Must read from the same instance the AuthContext writes to,
  // otherwise this non-reactive accessor returns stale/empty data.
  const sponsorId = sessionScopedStorage.getString("sponsorId")
  if (!sponsorId) return undefined
  return sponsorId as SponsorT
}
